/**
 * 投放落地页管理 —— 前后端共享 Zod 契约（ADM-LP-*，全局模块）
 *
 * 契约源：dev-docs `02-API契约/AE-投放落地页.md` §1 + `03-数据字典/120-投放落地页.md` §1/§2。
 * 覆盖 ADM-LP-S001（产品）+ S004（任务 CRUD）+ S006（文案配置）。
 * S003 模板清单 / S005 真生成 / S007 发布依赖 novel-campaign-pages，本轮生成 fail-closed。
 *
 * **全局模块**：四个产品端点均不按 `X-App-Id` 切分，body / query 不接收 `app_id`
 * （产品与 App 的关联走 `app_ids` 数组，落 `landing_product_apps`）。
 */

import { z } from 'zod';

/** 复用既有生成失败状态，明确区分配置变更与生成器失败。 */
export const LANDING_CONFIG_CHANGED_MESSAGE = '配置已修改，请重新生成后再下载或发布';

/** 主题身份色 5 键（缺任一生成器直接失败，故写侧预校验为必填）*/
export const LANDING_THEME_IDENTITY_KEYS = [
  'bg',
  'primary',
  'primary_deep',
  'primary_dark',
  'primary_soft',
] as const;

/** 主题中性色 5 键（仅产品需偏离通用值时配置）*/
export const LANDING_THEME_NEUTRAL_KEYS = [
  'surface',
  'fg',
  'fg_muted',
  'fg_subtle',
  'line',
] as const;

/** CSS 颜色值：#RGB / #RRGGBB / #RRGGBBAA —— 生成器按十六进制注入 CSS 变量 */
const HexColorSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    '必须是 #RGB / #RRGGBB / #RRGGBBAA',
  );

export const LandingThemeSchema = z
  .object({
    bg: HexColorSchema,
    primary: HexColorSchema,
    primary_deep: HexColorSchema,
    primary_dark: HexColorSchema,
    primary_soft: HexColorSchema,
    surface: HexColorSchema.optional(),
    fg: HexColorSchema.optional(),
    fg_muted: HexColorSchema.optional(),
    fg_subtle: HexColorSchema.optional(),
    line: HexColorSchema.optional(),
  })
  .strict();

/**
 * https URL —— 契约 AE §1.1 对 `legal_*` / `onelink_base_url` 只写「string (https URL)」。
 * 这里不额外收窄（不禁 query / fragment）：代码不得窄于 dev-docs，多余的拒绝会让契约合法的取值落到
 * `VALIDATION_FAILED`。`base_url` 另有闭集，理由见 `LandingBaseUrlSchema`。
 *
 * 本包零 runtime 依赖（server 与 web 共用，`lib` 不含 DOM/WebWorker），故用正则而非 `new URL()`。
 * 但正则必须校验**结构**而不只是前缀：只查 `https://` + 非空白后缀时，`https://?` 与
 * `https:///` 这类无 host 的串会通过，落库后拼成坏的页脚链接。故要求至少一个域名标签 +
 * 字母 TLD，端口与 path / query / fragment 可选。
 */
const HttpsUrlSchema = z
  .string()
  .max(512)
  .regex(
    // 端口段限 1..65535：`\d{1,5}` 会放过 `:99999`，URL 解析器一律拒，落库即坏链
    /^https:\/\/(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}(?::(?:[1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5]))?(?:[/?#][^\s]*)?$/,
    '必须是 https URL（含有效域名；带端口时须在 1..65535）',
  );

/**
 * logo 素材引用的 R2 key。**必须落在 admin 自有 namespace 内**（`P1-storage-layout`
 * 「admin 独有资源统一 `admin/` 一级 namespace」）—— 不加这道闸，任何写权限调用方都能把
 * `users/{uid}/avatar.webp` 这类 C 端私有对象填进来，生成器解析后会把别人的资产嵌进投放页。
 * 段内禁 `.` 开头是防 `..` 上跳出 namespace（key 由前端提交，不是服务端签发的不透明串）。
 */
const LANDING_LOGO_R2_KEY_RE =
  /^admin\/landing-products\/(?:[A-Za-z0-9_-][A-Za-z0-9._-]*\/)*[A-Za-z0-9_-][A-Za-z0-9._-]*$/;

/**
 * logo 素材引用。存储形态归 admin 实施（契约 AE §1.1），调用生成器时解析为
 * `{path}` / `{url}` / `{base64}`；本轮承载 R2 对象 key + 可选展示用直链。
 */
export const LandingLogoSchema = z
  .object({
    r2_key: z
      .string()
      .max(512)
      .regex(LANDING_LOGO_R2_KEY_RE, 'logo 必须是 admin/landing-products/ 下的对象 key'),
    // **必须走 HttpsUrlSchema，不能只 `.url()`**：zod 的 `.url()` 用 `new URL()` 判定，不限
    // scheme 也不限 host —— `javascript:alert(1)` / `data:text/html,…` / `http://evil.example`
    // 全部通过。而本值是 logo 素材引用的另一半，生成器会把它解析成 `{url}` 注入公开投放页
    // （见本对象 `r2_key` 上那道 namespace 闸：闸住了一半，另一半漏挂等于没闸），
    // 且会被 audit 的 before / after 各复制一份。同域其余 URL 字段一律 https + 域名结构。
    url: HttpsUrlSchema.optional(),
  })
  .strict();

/**
 * 投放 Web 页域闭集。契约 AE §1.1 把 `base_url` 的取值定义为
 * `01-通用约定/60-环境与域名.md` §2「投放 Web 页域」——该表原文「**投放 Web 页域共两个**：
 * staging 与 dev 共用 `lp-dev.novellea.app`，prod 用 `lp.novellea.app`」。
 *
 * **必须校验 host、不能只校验「是个 https URL」**：`base_url` 不只是发布目标，AE §1.1 还规定
 * `tracking_enabled=true` 时 `tracking_endpoint` = 本值同域 `/collect` —— 放任意域名进来，
 * 该产品所有生成页会把终端用户埋点打到那个域。而本模块**豁免 operator 门槛**（AE 开篇），
 * 最低权限的 viewer 就能写这个字段，故这道闸是本值唯一的写入侧把关。
 *
 * 与 §2 渠道白名单同体例：**服务端内置常量，条目变更走 dev-docs PR**（见
 * `lib/official-media-sources.ts`）。`localhost` 覆盖 §2 表里 dev 行的「本地 preview」。
 */
export const LANDING_BASE_URL_HOSTS = [
  'lp-dev.novellea.app',
  'lp.novellea.app',
  'localhost',
  '127.0.0.1',
] as const;

/**
 * 投放页域名基址。发布目标由 `<base_url>/<task_id>/` 拼出（契约 120 §1），故除 host 闭集外
 * 还要求**没有 path / query / fragment**：`https://lp.novellea.app/` 这种从地址栏复制来的
 * 尾斜杠值会拼出 `https://lp.novellea.app//<task_id>/`，双斜杠在静态托管上是另一条路径，
 * 发布完打开就是 404，而该值正是 `published_url` 的唯一事实源（120 §3）。
 * 「无 path / query / fragment」这一条契约未写，属实现必要性补充，随本域 dev-docs PR 补录。
 */
export function isLandingBaseUrl(value: string): boolean {
  // 只允许 `https://<闭集 host>`，本地 preview 两个 host 额外允许端口（1..65535）
  const match = /^https:\/\/([^\s/?#:]+)(?::(\d{1,5}))?$/.exec(value);
  if (!match) return false;
  const [, host, port] = match;
  if (!LANDING_BASE_URL_HOSTS.includes(host as (typeof LANDING_BASE_URL_HOSTS)[number]))
    return false;
  if (port === undefined) return true;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  const portNumber = Number(port);
  return isLocal && portNumber >= 1 && portNumber <= 65535;
}

export const LandingBaseUrlSchema = z
  .string()
  .max(512)
  .refine(
    isLandingBaseUrl,
    `base_url 必须是投放 Web 页域本身、不带 path / query / fragment，闭集：${LANDING_BASE_URL_HOSTS.map(
      (h) => `https://${h}`,
    ).join(' / ')}（仅本地 preview 可带端口）`,
  );

/**
 * AppsFlyer OneLink **template**：域名 + 模板 ID 两段（如 `https://xxx.onelink.me/AbCd`）。
 * custom link（三段及以上 path，如 `.../AbCd/mycampaign`）会被生成器拒绝，故此处同规则预校验。
 */
export const OnelinkTemplateUrlSchema = HttpsUrlSchema.regex(
  /^https:\/\/[^\s/?#]+\/[^\s/?#]+\/?$/,
  'OneLink 必须是 template 形态（域名 + 模板 ID 两段），custom link 不被生成器接受',
);

/** 产品关联去重后恰好一个 App，保留数组 wire 兼容既有调用方（AE §1）。 */
export const LANDING_PRODUCT_MAX_APP_IDS = 1;
/** 去重前的原始数组长度上限：先封顶再归一，超长重复数组不进 transform / Set 分配。 */
const LANDING_PRODUCT_APP_IDS_MAX_INPUT_LENGTH = 1000;
const LandingProductAppIdsSchema = z
  .array(z.string().uuid())
  .max(LANDING_PRODUCT_APP_IDS_MAX_INPUT_LENGTH, '投放产品必须关联恰好一个 App')
  .transform((ids) => [...new Set(ids.map((id) => id.toLowerCase()))])
  .refine((ids) => ids.length === LANDING_PRODUCT_MAX_APP_IDS, '投放产品必须关联恰好一个 App');

export const CreateLandingProductRequestSchema = z
  .object({
    name: z.string().min(1).max(128),
    app_ids: LandingProductAppIdsSchema,
    brand: z.string().min(1).max(128),
    logo: LandingLogoSchema.nullable().optional(),
    onelink_base_url: OnelinkTemplateUrlSchema.nullable().optional(),
    legal_privacy_url: HttpsUrlSchema,
    legal_terms_url: HttpsUrlSchema,
    /**
     * 客服反馈邮箱。可空 —— 配了才在投放页渲染客服悬浮按钮。
     * 长度上限 254 = RFC 5321 的邮件地址上限，与 DB 列宽一致。
     * 它会被生成器拼进 `mailto:` 注入公开投放页，故形态必须严格（同域其余 URL 字段的口径）。
     */
    support_email: z.string().email().max(254).nullable().optional(),
    theme: LandingThemeSchema,
    base_url: LandingBaseUrlSchema,
    tracking_enabled: z.boolean().optional(),
  })
  .strict();

/** PATCH 可改创建的全部字段（含 `app_ids` 整组替换，契约 AE §1.2）；至少给一个 */
export const UpdateLandingProductRequestSchema = CreateLandingProductRequestSchema.partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, '至少提供一个待更新字段');

export type CreateLandingProductRequest = z.infer<typeof CreateLandingProductRequestSchema>;
export type UpdateLandingProductRequest = z.infer<typeof UpdateLandingProductRequestSchema>;
export type LandingTheme = z.infer<typeof LandingThemeSchema>;
export type LandingLogo = z.infer<typeof LandingLogoSchema>;

/** 契约 AE §3「当前键闭集」——生成器接上后以 `--templates` copy 段为权威 */
export const LANDING_COPY_KEYS = [
  'cta',
  'hook_line1',
  'hook_line2',
  'topbar_tagline',
  'topbar_cta',
] as const;

export const LandingCopySchema = z
  .object({
    cta: z.string().max(500),
    hook_line1: z.string().max(500),
    hook_line2: z.string().max(500),
    topbar_tagline: z.string().max(500),
    topbar_cta: z.string().max(500),
  })
  .strict();

export type LandingCopy = z.infer<typeof LandingCopySchema>;

const LANDING_TASK_ASSET_R2_KEY_RE =
  /^admin\/landing-pages\/(?:[A-Za-z0-9_-][A-Za-z0-9._-]*\/)*[A-Za-z0-9_-][A-Za-z0-9._-]*$/;

export const LandingTaskAssetSchema = z
  .object({
    r2_key: z
      .string()
      .max(512)
      .regex(LANDING_TASK_ASSET_R2_KEY_RE, '素材必须是 admin/landing-pages/ 下的对象 key'),
    url: HttpsUrlSchema.optional(),
  })
  .strict();

export const LandingTemplateIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/, 'template_id 仅字母数字与 - _');

export const CreateLandingPageRequestSchema = z
  .object({
    product_id: z.string().uuid(),
    novel_id: z.string().uuid(),
    start_chapter: z.number().int().min(1).max(10_000).optional(),
    chapter_count: z.number().int().min(1).max(200).optional(),
    media_source: z.string().min(1).max(256),
    template_id: LandingTemplateIdSchema,
    show_topbar: z.boolean().optional(),
    cover: LandingTaskAssetSchema.nullable().optional(),
    banner: LandingTaskAssetSchema.nullable().optional(),
    copy_overrides: LandingCopySchema,
  })
  .strict();

export const UpdateLandingPageRequestSchema = CreateLandingPageRequestSchema.partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, '至少提供一个待更新字段');

export const PutLandingCopyRequestSchema = z
  .object({
    copy: LandingCopySchema,
  })
  .strict();

/**
 * 任务列表的可选过滤（AE §4.1）：按书 / 按渠道，AND 关系。
 *
 * 投放计划的关联表单要的候选集正是这一对（AF §4「候选集 = 该书下同渠道的落地页任务」）——
 * 只能客户端过滤的话，总数超过 limit 上限后会静默漏掉合法候选、运营根本关联不上。
 */
export const ListLandingPagesQuerySchema = z.object({
  novel_id: z.string().uuid().optional(),
  media_source: z.string().min(1).max(256).optional(),
});
export type ListLandingPagesQuery = z.infer<typeof ListLandingPagesQuerySchema>;

export type CreateLandingPageRequest = z.infer<typeof CreateLandingPageRequestSchema>;
export type UpdateLandingPageRequest = z.infer<typeof UpdateLandingPageRequestSchema>;
export const LandingArtifactFileSchema = z
  .object({
    path: z
      .string()
      .min(1)
      .max(512)
      .regex(/^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9._/-]+$/, '产物路径非法'),
    sha256: z.string().regex(/^[a-f0-9]{64}$/i, 'sha256 必须是 64 位十六进制'),
  })
  .strict();

export const LandingArtifactFilesSchema = z
  .array(LandingArtifactFileSchema)
  .min(1)
  .refine(
    (files) => files.some((f) => f.path === 'index.html' || f.path.endsWith('/index.html')),
    '产物必须包含 index.html',
  );

export type PutLandingCopyRequest = z.infer<typeof PutLandingCopyRequestSchema>;
export type LandingTaskAsset = z.infer<typeof LandingTaskAssetSchema>;
export type LandingArtifactFile = z.infer<typeof LandingArtifactFileSchema>;

export type LandingGenerateStatus = 'GENERATING' | 'GENERATED' | 'GENERATE_FAILED';
export type LandingPublishStatus = 'PUBLISHING' | 'PUBLISH_FAILED';

export interface LandingPageDto {
  created_by?: string | null;
  creator_account_code?: string | null;
  id: string;
  task_id: string;
  product_id: string;
  novel_id: string;
  start_chapter: number;
  chapter_count: number;
  media_source: string;
  template_id: string;
  show_topbar: boolean;
  cover: LandingTaskAsset | null;
  banner: LandingTaskAsset | null;
  copy_overrides: LandingCopy;
  generate_status: LandingGenerateStatus;
  generate_error: string | null;
  publish_status: LandingPublishStatus | null;
  published_url: string | null;
  artifact_path: string | null;
  artifact_files: LandingArtifactFile[] | null;
  has_first_chapter_preview: boolean;
  created_at: string;
  updated_at: string;
}

export interface LandingCopyDto {
  source: 'saved' | 'default';
  copy: LandingCopy;
}

/** 产品 DTO（AE §1.1「`data` = 产品完整 DTO」）*/
export interface LandingProductDto {
  id: string;
  name: string;
  app_ids: string[];
  brand: string;
  logo: LandingLogo | null;
  onelink_base_url: string | null;
  legal_privacy_url: string;
  legal_terms_url: string;
  /** 客服反馈邮箱；null = 未配，投放页不渲染客服悬浮按钮 */
  support_email: string | null;
  theme: LandingTheme;
  base_url: string;
  tracking_enabled: boolean;
  /** 产品 PATCH 后的实际 HTML 影响，供确认重生成；GET 与旧客户端可省略。 */
  html_change?: {
    html_inputs_changed: boolean;
    affected_projects: { project_id: string; task_ids: string[] }[];
    affected_legacy_task_count: number;
    reason: string;
  };
  created_at: string;
  updated_at: string;
}
